import { LiksTooltip } from '../../../components/liks/LiksTooltip/LiksTooltip';
import { getDocumentLabel } from '../../../features/knowledge/documentActionEligibility';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type {
  DocumentEmbeddingProgress,
  WithResourceId,
} from '../../../types/knowledge';
import {
  formatBytes,
  formatFileType,
  formatNumber,
  formatTimestamp,
} from '../../../utils/formatUtils';
import { KnowledgeDocumentTableActions } from '../KnowledgeDocumentTableActions';
import { KnowledgeDocumentUrlCell } from '../KnowledgeDocumentUrlCell';

type DocumentRow = WithResourceId<KnowledgeBaseDocument>;

type KnowledgeDocumentTableRowProps = {
  document: DocumentRow;
  locale: string;
  canManageDocuments: boolean;
  isSelected: boolean;
  hasSelection: boolean;
  isBulkActionRunning: boolean;
  embeddingProgress: DocumentEmbeddingProgress | undefined;
  translate: (key: string, params?: Record<string, string>) => string;
  onToggleSelected: (documentId: string) => void;
  onPreview: (documents: DocumentRow[]) => void;
  onDownload: (document: DocumentRow) => void;
  onResume: (document: DocumentRow) => void;
  onRetry: (documents: DocumentRow[]) => void;
  onDelete: (documents: DocumentRow[]) => void;
};

export function KnowledgeDocumentTableRow({
  document,
  locale,
  canManageDocuments,
  isSelected,
  hasSelection,
  isBulkActionRunning,
  embeddingProgress,
  translate,
  onToggleSelected,
  onPreview,
  onDownload,
  onResume,
  onRetry,
  onDelete,
}: KnowledgeDocumentTableRowProps) {
  const documentLabel = getDocumentLabel(document);
  const status = document.status ?? 'PENDING';
  const statusLabel = translate(`knowledge.documentStatus.${status}`);
  const isEmbedding = status === 'EMBEDDING';
  const hasEmbeddingChunkTarget =
    isEmbedding &&
    embeddingProgress?.totalChunks != null &&
    embeddingProgress.totalChunks > 0;
  let statusDisplayLabel = statusLabel;
  if (isEmbedding && !hasEmbeddingChunkTarget) {
    statusDisplayLabel = translate('knowledge.documentEmbeddingAnalyzing');
  } else if (hasEmbeddingChunkTarget && embeddingProgress?.percentage != null) {
    statusDisplayLabel = `${statusLabel} (${Math.round(embeddingProgress.percentage)}%)`;
  }
  const embeddingProgressTooltip =
    hasEmbeddingChunkTarget && embeddingProgress?.embeddedChunks != null
      ? translate('knowledge.documentEmbeddingProgressTooltip', {
          embedded: formatNumber(embeddingProgress.embeddedChunks, locale),
          total: formatNumber(embeddingProgress.totalChunks, locale),
        })
      : undefined;
  const statusBadge = (
    <span
      className={`knowledge-database__status knowledge-database__status--${status.toLowerCase()}`}
    >
      {statusDisplayLabel}
    </span>
  );

  return (
    <tr
      className={
        isSelected ? 'knowledge-database__table-row--selected' : undefined
      }
    >
      <td className="knowledge-database__select-cell knowledge-database__col-select">
        <input
          type="checkbox"
          className="knowledge-database__select-checkbox"
          checked={isSelected}
          aria-label={translate('knowledge.table.selectDocument', {
            fileName: documentLabel,
          })}
          onChange={() => onToggleSelected(document.id)}
        />
      </td>
      <td className="knowledge-database__file-cell knowledge-database__col-file">
        <LiksTooltip content={documentLabel} placement="top-start" block>
          <strong className="knowledge-database__file-name">
            {documentLabel}
          </strong>
        </LiksTooltip>
      </td>
      <td className="knowledge-database__col-size">
        {formatBytes(document.fileSize ?? 0, locale)}
      </td>
      <td className="knowledge-database__col-type">
        {formatFileType(
          document.fileName ?? documentLabel,
          document.mimeType ?? 'application/octet-stream',
        )}
      </td>
      <td className="knowledge-database__col-status">
        {embeddingProgressTooltip != null ? (
          <LiksTooltip content={embeddingProgressTooltip} placement="top-start">
            {statusBadge}
          </LiksTooltip>
        ) : (
          statusBadge
        )}
      </td>
      <td className="knowledge-database__col-uploaded">
        {document.created
          ? formatTimestamp(document.created, locale)
          : translate('knowledge.notAvailable')}
      </td>
      <td className="knowledge-database__col-modified">
        {document.stamp
          ? formatTimestamp(document.stamp, locale)
          : translate('knowledge.notAvailable')}
      </td>
      <td className="knowledge-database__col-source">
        {document.sourceType
          ? translate(`knowledge.sourceType.${document.sourceType}`)
          : translate('knowledge.notAvailable')}
      </td>
      <td className="knowledge-database__url-cell knowledge-database__col-url">
        {document.sourceUrl ? (
          <KnowledgeDocumentUrlCell
            url={document.sourceUrl}
            copyLabel={translate('knowledge.copySourceUrl')}
            copiedLabel={translate('knowledge.copySourceUrlCopied')}
          />
        ) : (
          translate('knowledge.notAvailable')
        )}
      </td>
      <td className="knowledge-database__actions-cell knowledge-database__col-actions">
        {hasSelection ? null : (
          <KnowledgeDocumentTableActions
            mode="row"
            documents={[document]}
            canManageDocuments={canManageDocuments}
            disabled={isBulkActionRunning}
            translate={translate}
            onPreview={onPreview}
            onDownload={(docs) => {
              const target = docs[0];
              if (target) {
                onDownload(target);
              }
            }}
            onResume={(docs) => {
              const target = docs[0];
              if (target) {
                onResume(target);
              }
            }}
            onRetry={onRetry}
            onDelete={onDelete}
          />
        )}
      </td>
    </tr>
  );
}
